import { useEffect, useState, type FormEvent } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useFavorites } from "@/hooks/useFavorites";

const navItems = [
  { label: "首页", to: "/", end: true },
  { label: "电影", to: "/category/movie", end: false },
  { label: "电视剧", to: "/category/tv", end: false },
  { label: "综艺", to: "/category/variety", end: false },
  { label: "资讯", to: "/news", end: false },
];

export default function SiteHeader() {
  const location = useLocation();
  const navigate = useNavigate();
  const { total } = useFavorites();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState("");

  const isHome = location.pathname === "/";
  const transparent = isHome && !scrolled;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  const submitSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = keyword.trim();
    if (!value) {
      navigate("/search");
      return;
    }
    navigate(`/search?q=${encodeURIComponent(value)}`);
    setKeyword("");
    setMenuOpen(false);
  };

  const brandText = transparent ? "text-background-50" : "text-foreground-950";
  const navText = transparent
    ? "text-background-50/80 hover:text-background-50"
    : "text-foreground-700 hover:text-primary-600";
  const navActive = transparent ? "text-background-50" : "text-primary-600";

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
          transparent
            ? "bg-transparent"
            : "border-b border-background-200 bg-background-50/95 backdrop-blur-md"
        }`}
      >
        <div className="flex h-[72px] w-full items-center gap-6 px-4 md:px-8">
          <Link to="/" className="flex shrink-0 cursor-pointer items-center gap-2.5">
            <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-500 text-background-50">
              <i className="ri-leaf-line text-lg" />
            </span>
            <span className={`flex flex-col leading-none ${brandText}`}>
              <span className="font-heading text-xl font-bold tracking-tight">枫与铃</span>
              <span className="mt-1 text-[9px] uppercase tracking-[0.3em] opacity-70">
                Feng &amp; Ling
              </span>
            </span>
          </Link>

          <nav className="hidden items-center gap-1 lg:flex" aria-label="主导航">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  `cursor-pointer whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                    isActive ? navActive : navText
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <Link
              to="/#topics"
              className={`cursor-pointer whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-colors ${navText}`}
            >
              专题
            </Link>
          </nav>

          <div className="ml-auto flex items-center gap-2 md:gap-3">
            <form
              onSubmit={submitSearch}
              className={`hidden items-center gap-2 rounded-full border px-3 py-1.5 transition-colors md:flex ${
                transparent
                  ? "border-background-50/30 bg-background-50/10 focus-within:border-background-50/60"
                  : "border-background-300 bg-background-100 focus-within:border-primary-400"
              }`}
            >
              <span
                className={`flex h-4 w-4 items-center justify-center ${
                  transparent ? "text-background-50/70" : "text-foreground-500"
                }`}
              >
                <i className="ri-search-line text-sm" />
              </span>
              <input
                type="search"
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索影视或资讯"
                aria-label="搜索影视或资讯"
                className={`w-36 bg-transparent text-sm outline-none xl:w-48 ${
                  transparent
                    ? "text-background-50 placeholder:text-background-50/60"
                    : "text-foreground-900 placeholder:text-foreground-400"
                }`}
              />
              <button
                type="submit"
                className={`cursor-pointer whitespace-nowrap text-xs font-medium ${
                  transparent ? "text-background-50/80 hover:text-background-50" : "text-primary-600"
                }`}
              >
                搜索
              </button>
            </form>

            <Link
              to="/favorites"
              aria-label="我的收藏"
              className={`relative flex h-9 w-9 cursor-pointer items-center justify-center rounded-full transition-colors ${
                transparent
                  ? "bg-background-50/10 text-background-50 hover:bg-background-50/20"
                  : "bg-background-100 text-foreground-700 hover:bg-background-200 hover:text-primary-600"
              }`}
            >
              <i className="ri-heart-3-line text-base" />
              {total > 0 ? (
                <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary-500 px-1 text-[10px] font-semibold text-background-50">
                  {total}
                </span>
              ) : null}
            </Link>

            <button
              type="button"
              onClick={() => setMenuOpen(true)}
              aria-label="打开菜单"
              className={`flex h-9 w-9 cursor-pointer items-center justify-center rounded-full transition-colors lg:hidden ${
                transparent
                  ? "bg-background-50/10 text-background-50"
                  : "bg-background-100 text-foreground-800"
              }`}
            >
              <i className="ri-menu-3-line text-lg" />
            </button>
          </div>
        </div>
      </header>

      {menuOpen ? (
        <div className="fixed inset-0 z-[70] flex flex-col bg-background-50 lg:hidden">
          <div className="flex h-[72px] shrink-0 items-center justify-between border-b border-background-200 px-4">
            <span className="font-heading text-lg font-bold text-foreground-950">枫与铃</span>
            <button
              type="button"
              onClick={() => setMenuOpen(false)}
              aria-label="关闭菜单"
              className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full bg-background-100 text-foreground-800"
            >
              <i className="ri-close-line text-xl" />
            </button>
          </div>
          <form onSubmit={submitSearch} className="shrink-0 px-4 pt-4">
            <div className="flex items-center gap-2 rounded-full border border-background-300 bg-background-100 px-4 py-2.5">
              <span className="flex h-4 w-4 items-center justify-center text-foreground-500">
                <i className="ri-search-line text-sm" />
              </span>
              <input
                type="search"
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索影视或资讯"
                aria-label="搜索影视或资讯"
                className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
              />
              <button type="submit" className="cursor-pointer text-sm font-medium text-primary-600">
                搜索
              </button>
            </div>
          </form>
          <nav className="mt-4 flex-1 overflow-y-auto px-4 pb-8" aria-label="移动端导航">
            {[...navItems, { label: "专题", to: "/#topics", end: false }, { label: "我的收藏", to: "/favorites", end: false }].map(
              (item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className="flex cursor-pointer items-center justify-between border-b border-background-200 py-4 text-base font-medium text-foreground-900"
                >
                  {item.label}
                  <span className="flex h-5 w-5 items-center justify-center text-foreground-400">
                    <i className="ri-arrow-right-s-line text-lg" />
                  </span>
                </Link>
              )
            )}
          </nav>
          <div className="shrink-0 border-t border-background-200 px-4 py-4 text-xs text-foreground-500">
            演示站点 · 不含播放与下载功能
          </div>
        </div>
      ) : null}
    </>
  );
}